import { useMemo } from 'react';

interface Props {
  open: boolean;
  title: string;
  fileName: string;
  previewUrl: string;
  mimeType?: string;
  loading?: boolean;
  error?: string;
  zoom: number;
  onDownload: () => void;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}

export default function DocumentViewerModal({
  open,
  title,
  fileName,
  previewUrl,
  mimeType = '',
  loading = false,
  error = '',
  zoom,
  onDownload,
  onAccept,
  onDecline,
  onClose,
}: Props) {
  const kind = useMemo(() => {
    const lower = String(mimeType || '').toLowerCase();
    if (lower.startsWith('image/')) return 'image';
    if (lower.includes('pdf')) return 'pdf';
    return 'other';
  }, [mimeType]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950/70 backdrop-blur-[1px] flex items-end justify-center p-2 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-6xl h-[calc(100dvh-1rem)] sm:h-[88vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col gap-3 px-4 py-3 border-b border-slate-200 shrink-0 sm:flex-row sm:items-center sm:px-5 sm:py-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>
            <p className="text-xs text-slate-500 truncate mt-0.5">{fileName}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={onDownload} className="min-h-[40px] px-3 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line mr-1" /> Download
            </button>
            <button type="button" onClick={onClose} className="h-10 w-10 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-slate-100 overflow-auto">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">Loading preview…</div>
          ) : error ? (
            <div className="h-full flex items-center justify-center px-6">
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">{error}</p>
                <p className="text-xs text-slate-500 mt-2">You can still download the file to review it.</p>
              </div>
            </div>
          ) : kind === 'image' && previewUrl ? (
            <div className="min-h-full flex items-start justify-center p-3 sm:p-6">
              <img
                src={previewUrl}
                alt={fileName}
                className="max-w-full sm:max-w-none rounded-xl shadow-lg bg-white"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
              />
            </div>
          ) : kind === 'pdf' && previewUrl ? (
            <div className="h-full p-2 sm:p-4">
              <iframe
                title={fileName}
                src={`${previewUrl}#zoom=${Math.round(zoom * 100)}`}
                className="w-full h-full rounded-xl bg-white border border-slate-200"
              />
            </div>
          ) : (
            <div className="h-full flex items-center justify-center px-6">
              <div className="text-center">
                <i className="ri-file-line text-5xl text-slate-300" />
                <p className="text-sm font-medium text-slate-700 mt-3">Preview not available for this file type.</p>
                <p className="text-xs text-slate-500 mt-1">Use Download to inspect the document.</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 px-4 py-3 border-t border-slate-200 shrink-0 min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-end sm:px-5 sm:py-4">
          <button type="button" onClick={onDecline} className="min-h-[44px] px-4 rounded-lg border border-rose-200 text-rose-600 font-medium hover:bg-rose-50 cursor-pointer whitespace-nowrap">
            Decline
          </button>
          <button type="button" onClick={onAccept} className="min-h-[44px] px-4 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 cursor-pointer whitespace-nowrap">
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
